"use client";

import { useCallback, useEffect, useReducer } from "react";

import {
  demoTourReducer,
  INITIAL_TOUR_STATE,
  isTourPlaying,
  type DemoTourMode,
} from "../lib/demo-tour-reducer";
import type { DemoScript } from "../types";
import { useDemoPlayer } from "./use-demo-player";

export interface UseDemoTourReturn {
  readonly mode: DemoTourMode;
  readonly isPlaying: boolean;
  readonly player: ReturnType<typeof useDemoPlayer>;
  readonly startTour: () => void;
  readonly exitTour: () => void;
  readonly togglePause: () => void;
  readonly handleStageInteraction: () => void;
  readonly goToHotspot: (stepIndex: number) => void;
}

interface UseDemoTourOptions {
  readonly script: DemoScript;
  readonly onScriptComplete: () => void;
  readonly autoPlayWhen: boolean;
  /**
   * Se `true` (padrão), `prefers-reduced-motion: reduce` bloqueia o autoplay.
   * A página `/demo` tem botão para iniciar o tour na mão, então pode manter
   * o guard. A seção da home não tem esse botão — desligar aqui deixaria a
   * seção congelada no passo 0 sem saída nenhuma, então a home passa `false`.
   */
  readonly respectReducedMotion?: boolean;
}

export function useDemoTour({
  script,
  onScriptComplete,
  autoPlayWhen,
  respectReducedMotion = true,
}: UseDemoTourOptions): UseDemoTourReturn {
  const [state, dispatch] = useReducer(demoTourReducer, INITIAL_TOUR_STATE);

  const isPlaying = isTourPlaying(state);

  const player = useDemoPlayer({
    script,
    autoPlay: isPlaying,
    // Sem tour não existe "próxima tela": no modo livre o roteiro termina e
    // fica onde está, esperando o próximo clique.
    onComplete: isPlaying ? onScriptComplete : undefined,
  });

  const startTour = useCallback(() => dispatch({ type: "start-tour" }), []);
  const exitTour = useCallback(() => dispatch({ type: "exit-tour" }), []);
  const togglePause = useCallback(() => dispatch({ type: "toggle-pause" }), []);
  const handleStageInteraction = useCallback(
    () => dispatch({ type: "stage-interaction" }),
    [],
  );

  const goToHotspot = useCallback(
    (stepIndex: number) => {
      dispatch({ type: "stage-interaction" });
      player.goToStep(stepIndex);
    },
    [player],
  );

  /*
   * O estado inicial é sempre o modo livre, e quem toca sozinho liga por aqui.
   * Ler `autoPlayWhen` no valor inicial do reducer não serviria: na home ele é
   * `false` na montagem e só vira `true` quando a seção entra na viewport, e o
   * valor inicial de um `useReducer` é lido uma vez.
   *
   * `prefers-reduced-motion` não ganha tour automático quando existe botão
   * para compensar: quem pediu menos movimento não quer câmera andando
   * sozinha, mas ainda pode iniciar na mão. Na home (`respectReducedMotion:
   * false`) não há esse botão, então o guard fica de fora e a seção toca
   * normalmente, como sempre tocou.
   */
  useEffect(() => {
    if (!autoPlayWhen) return;
    if (
      respectReducedMotion &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    ) {
      return;
    }
    dispatch({ type: "start-tour" });
  }, [autoPlayWhen, respectReducedMotion]);

  return {
    mode: state.mode,
    isPlaying,
    player,
    startTour,
    exitTour,
    togglePause,
    handleStageInteraction,
    goToHotspot,
  };
}
